/**
 * Task Service - CRUD operations for tasks
 * Handles tasks, subtasks, and task-tag relationships
 */

import { supabase } from '../../supabaseClient';
import { transformTaskFromDB, transformTaskToDB, transformSubtaskToDB } from '../utils/transformers';

/**
 * Helper: Convert "default" project ID to actual default project UUID
 */
async function resolveProjectId(projectId) {
  if (projectId !== 'default') return projectId;

  // Fetch the user's default project (Inbox)
  const { data, error } = await supabase
    .from('projects')
    .select('id')
    .eq('is_default', true)
    .is('deleted_at', null)
    .single();

  if (error || !data) {
    throw new Error('Default project (Inbox) not found. Please create it first.');
  }

  return data.id;
}

/**
 * Fetch all tasks for current user
 */
export async function fetchAll() {
  try {
    const { data, error } = await supabase
      .from('tasks')
      .select(`
        *,
        subtasks!subtasks_task_id_fkey(*),
        task_tags!task_tags_task_id_fkey(tag_id)
      `)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data: data.map(transformTaskFromDB)
    };
  } catch (error) {
    console.error('[taskService.fetchAll] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Fetch tasks for a specific project
 */
export async function fetchByProject(projectId) {
  try {
    const { data, error } = await supabase
      .from('tasks')
      .select(`
        *,
        subtasks!subtasks_task_id_fkey(*),
        task_tags!task_tags_task_id_fkey(tag_id)
      `)
      .eq('project_id', projectId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false});

    if (error) throw error;

    return {
      success: true,
      data: data.map(transformTaskFromDB)
    };
  } catch (error) {
    console.error('[taskService.fetchByProject] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Create a new task with client_request_id for idempotence
 */
export async function create(task, clientRequestId = null) {
  const requestId = clientRequestId || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  try {
    // Resolve "default" to actual default project UUID
    const resolvedProjectId = await resolveProjectId(task.projectId);
    const taskWithResolvedProject = { ...task, projectId: resolvedProjectId };

    // Insert task
    const { data: taskData, error: taskError } = await supabase
      .from('tasks')
      .insert([{
        ...transformTaskToDB(taskWithResolvedProject),
        client_request_id: requestId
      }])
      .select()
      .single();

    if (taskError) {
      // Check for duplicate (unique violation on client_request_id)
      if (taskError.code === '23505' && taskError.message.includes('client_request_id')) {
        console.log('[taskService.create] Duplicate detected, fetching existing task');

        // Fetch existing task
        const { data: existing, error: fetchError } = await supabase
          .from('tasks')
          .select(`
            *,
            subtasks!subtasks_task_id_fkey(*),
            task_tags!task_tags_task_id_fkey(tag_id)
          `)
          .eq('client_request_id', requestId)
          .single();

        if (fetchError) throw fetchError;

        return {
          success: true,
          data: transformTaskFromDB(existing),
          isDuplicate: true
        };
      }

      throw taskError;
    }

    // Handle subtasks if present
    if (task.subTasks && task.subTasks.length > 0) {
      const subtasksToInsert = task.subTasks.map((subtask, index) => ({
        ...transformSubtaskToDB(subtask),
        task_id: taskData.id,
        position: index
      }));

      const { error: subtasksError } = await supabase
        .from('subtasks')
        .insert(subtasksToInsert);

      if (subtasksError) {
        console.error('[taskService.create] Subtasks error:', subtasksError);
        // Continue even if subtasks fail - task is already created
      }
    }

    // Handle tags if present
    if (task.tags && task.tags.length > 0) {
      const taskTagsToInsert = task.tags.map(tagId => ({
        task_id: taskData.id,
        tag_id: tagId
      }));

      const { error: tagsError } = await supabase
        .from('task_tags')
        .insert(taskTagsToInsert);

      if (tagsError) {
        console.error('[taskService.create] Tags error:', tagsError);
        // Continue even if tags fail - task is already created
      }
    }

    // Fetch complete task with relations
    const { data: completeTask, error: fetchError } = await supabase
      .from('tasks')
      .select(`
        *,
        subtasks!subtasks_task_id_fkey(*),
        task_tags!task_tags_task_id_fkey(tag_id)
      `)
      .eq('id', taskData.id)
      .single();

    if (fetchError) throw fetchError;

    return {
      success: true,
      data: transformTaskFromDB(completeTask)
    };
  } catch (error) {
    console.error('[taskService.create] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Update a task
 */
export async function update(taskId, updates) {
  try {
    // Resolve "default" project ID if present
    let updatesToApply = updates;
    if (updates.projectId) {
      const resolvedProjectId = await resolveProjectId(updates.projectId);
      updatesToApply = { ...updates, projectId: resolvedProjectId };
    }

    // Update main task
    const { error: taskError } = await supabase
      .from('tasks')
      .update(transformTaskToDB(updatesToApply))
      .eq('id', taskId)
      .select()
      .single();

    if (taskError) throw taskError;

    // Handle subtasks update if present
    if (updates.subTasks) {
      // Get current subtasks from DB to reconcile changes
      const { data: currentSubtasks, error: fetchError } = await supabase
        .from('subtasks')
        .select('id')
        .eq('task_id', taskId);

      if (fetchError) {
        console.error('[taskService.update] Error fetching current subtasks:', fetchError);
      }

      const currentIds = (currentSubtasks || []).map(st => st.id);
      const updatedIds = updates.subTasks
        .filter(st => st.id && !st.id.toString().startsWith('temp-'))
        .map(st => st.id);

      // Delete subtasks that are no longer in the list (hard delete to avoid accumulation)
      const idsToDelete = currentIds.filter(id => !updatedIds.includes(id));
      if (idsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('subtasks')
          .delete()
          .in('id', idsToDelete);

        if (deleteError) {
          console.error('[taskService.update] Error deleting removed subtasks:', deleteError);
        }
      }

      // Upsert all subtasks (update existing, insert new)
      if (updates.subTasks.length > 0) {
        const subtasksToUpsert = updates.subTasks.map((subtask, index) => {
          const dbSubtask = {
            ...transformSubtaskToDB(subtask),
            task_id: taskId,
            position: index
          };

          // Only include ID if it's a real DB ID (not temp)
          if (subtask.id && !subtask.id.toString().startsWith('temp-')) {
            dbSubtask.id = subtask.id;
          }

          return dbSubtask;
        });

        const { error: upsertError } = await supabase
          .from('subtasks')
          .upsert(subtasksToUpsert, {
            onConflict: 'id',
            ignoreDuplicates: false
          });

        if (upsertError) {
          console.error('[taskService.update] Error upserting subtasks:', upsertError);
          throw upsertError;
        }
      }
    }

    // Handle tags update if present
    if (updates.tags !== undefined) {
      // Delete existing tag relationships
      await supabase
        .from('task_tags')
        .delete()
        .eq('task_id', taskId);

      // Insert new tag relationships
      if (updates.tags.length > 0) {
        const taskTagsToInsert = updates.tags.map(tagId => ({
          task_id: taskId,
          tag_id: tagId
        }));

        await supabase
          .from('task_tags')
          .insert(taskTagsToInsert);
      }
    }

    // Fetch complete updated task
    const { data: completeTask, error: fetchError } = await supabase
      .from('tasks')
      .select(`
        *,
        subtasks!subtasks_task_id_fkey(*),
        task_tags!task_tags_task_id_fkey(tag_id)
      `)
      .eq('id', taskId)
      .single();

    if (fetchError) throw fetchError;

    return {
      success: true,
      data: transformTaskFromDB(completeTask)
    };
  } catch (error) {
    console.error('[taskService.update] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Soft delete a task
 */
export async function deleteTask(taskId) {
  try {
    const { error } = await supabase
      .from('tasks')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', taskId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('[taskService.delete] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Toggle task completion status
 */
export async function toggleComplete(taskId, completed) {
  try {
    const updates = {
      completed,
      completed_at: completed ? new Date().toISOString() : null
    };

    const { data, error } = await supabase
      .from('tasks')
      .update(updates)
      .eq('id', taskId)
      .select(`
        *,
        subtasks!subtasks_task_id_fkey(*),
        task_tags!task_tags_task_id_fkey(tag_id)
      `)
      .single();

    if (error) throw error;

    return {
      success: true,
      data: transformTaskFromDB(data)
    };
  } catch (error) {
    console.error('[taskService.toggleComplete] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Update points status for a task
 */
export async function updatePoints(taskId, pointsData) {
  try {
    const { data, error } = await supabase
      .from('tasks')
      .update(pointsData)
      .eq('id', taskId)
      .select(`
        *,
        subtasks!subtasks_task_id_fkey(*),
        task_tags!task_tags_task_id_fkey(tag_id)
      `)
      .single();

    if (error) throw error;

    return {
      success: true,
      data: transformTaskFromDB(data)
    };
  } catch (error) {
    console.error('[taskService.updatePoints] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Archive a task
 */
export async function archiveTask(taskId) {
  try {
    const updates = {
      archived: true,
      archived_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('tasks')
      .update(updates)
      .eq('id', taskId)
      .select(`
        *,
        subtasks!subtasks_task_id_fkey(*),
        task_tags!task_tags_task_id_fkey(tag_id)
      `)
      .single();

    if (error) throw error;

    return {
      success: true,
      data: transformTaskFromDB(data)
    };
  } catch (error) {
    console.error('[taskService.archiveTask] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Unarchive a task (restore from archive)
 */
export async function unarchiveTask(taskId) {
  try {
    const updates = {
      archived: false,
      archived_at: null
    };

    const { data, error } = await supabase
      .from('tasks')
      .update(updates)
      .eq('id', taskId)
      .select(`
        *,
        subtasks!subtasks_task_id_fkey(*),
        task_tags!task_tags_task_id_fkey(tag_id)
      `)
      .single();

    if (error) throw error;

    return {
      success: true,
      data: transformTaskFromDB(data)
    };
  } catch (error) {
    console.error('[taskService.unarchiveTask] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Fetch all archived tasks for current user
 */
export async function fetchArchived() {
  try {
    const { data, error } = await supabase
      .from('tasks')
      .select(`
        *,
        subtasks!subtasks_task_id_fkey(*),
        task_tags!task_tags_task_id_fkey(tag_id)
      `)
      .eq('archived', true)
      .is('deleted_at', null)
      .order('archived_at', { ascending: false });

    if (error) throw error;

    return {
      success: true,
      data: data.map(transformTaskFromDB)
    };
  } catch (error) {
    console.error('[taskService.fetchArchived] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

export const taskService = {
  fetchAll,
  fetchByProject,
  create,
  update,
  delete: deleteTask,
  toggleComplete,
  updatePoints,
  archiveTask,
  unarchiveTask,
  fetchArchived
};
